import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Grid,
    IconButton,
    InputAdornment,
    Link,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import {
    CalendarMonth as CalendarMonthIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Download as DownloadIcon,
    AccountBalanceWallet as WalletIcon,
    MonetizationOn as MonetizationIcon,
    PersonAdd as PersonAddIcon,
    Refresh as RefreshIcon,
    Replay as ReplayIcon,
    Receipt as ReceiptIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import SubscriptionChargeDetailsDialog from './SubscriptionChargeDetailsDialog';

const REPORT_PATH = '/api/v1/content/admin/subscriptions/monthly-report';

const currentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const shiftMonth = (month, delta) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (month) => {
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const formatCurrency = (v) =>
    v == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);
const formatDateTime = (d) => (d ? new Date(d).toLocaleString() : '—');
const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const cycleStatusColor = (status) => {
    if (status === 'CLOSED') return 'default';
    if (status === 'SHIPPED') return 'success';
    return 'info';
};

const StatCard = ({ icon: Icon, label, value, caption, color }) => {
    const theme = useTheme();
    return (
        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <CardContent sx={{ py: 2.25, px: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: color, width: 40, height: 40 }}>
                        <Icon fontSize="small" />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 600 }}>
                            {label}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{value}</Typography>
                        {caption && <Typography variant="caption" color="text.secondary">{caption}</Typography>}
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

const SubscriptionReportView = () => {
    const theme = useTheme();
    const { getAccessToken } = useAuth();
    const API_BASE_URL = config.API_BASE_URL;

    const [searchParams, setSearchParams] = useSearchParams();
    const month = /^\d{4}-\d{2}$/.test(searchParams.get('month') || '') ? searchParams.get('month') : currentMonth();

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState(null);

    const [search, setSearch] = useState('');
    const [kind, setKind] = useState('ALL');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [selected, setSelected] = useState(null);

    const setMonth = (next) => {
        setSearchParams((current) => {
            const params = new URLSearchParams(current);
            params.set('month', next);
            return params;
        }, { replace: true });
    };

    const authHeaders = useCallback(async () => {
        const token = await getAccessToken();
        if (!token) throw new Error('Authentication token not available');
        return { Authorization: `Bearer ${token}` };
    }, [getAccessToken]);

    const loadReport = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE_URL}${REPORT_PATH}?month=${month}`, { headers: await authHeaders() });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body.message || `${res.status}`);
            setReport(body.data);
        } catch (err) {
            setReport(null);
            setError(`Failed to load the ${monthLabel(month)} report: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, authHeaders, month]);

    useEffect(() => {
        loadReport();
        setPage(0);
    }, [loadReport]);

    const handleExport = async () => {
        setExporting(true);
        try {
            const res = await fetch(`${API_BASE_URL}${REPORT_PATH}/export?month=${month}`, { headers: await authHeaders() });
            if (!res.ok) throw new Error(`Export failed: ${res.status}`);
            const url = window.URL.createObjectURL(await res.blob());
            const a = document.createElement('a');
            a.href = url;
            a.download = `subscription-charges-${month}.csv`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.message);
        } finally {
            setExporting(false);
        }
    };

    const rows = useMemo(() => {
        const all = report?.rows || [];
        const q = search.trim().toLowerCase();
        return all.filter((r) => {
            if (kind !== 'ALL' && r.chargeKind !== kind) return false;
            if (!q) return true;
            return [r.iboNumber, r.iboName, r.paymentId, r.bookOrderNumber, String(r.subscriptionId)]
                .some((v) => v && v.toLowerCase().includes(q));
        });
    }, [report, search, kind]);

    const visibleTotal = useMemo(
        () => rows.reduce((sum, r) => sum + (r.chargedAmount != null ? Number(r.chargedAmount) : 0), 0),
        [rows],
    );
    const filtered = rows.length !== (report?.rows?.length || 0);
    const pageRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    mb: 2.5,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    color: 'common.white',
                }}
            >
                <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ bgcolor: 'rgba(255,255,255,.18)' }}>
                            <CalendarMonthIcon />
                        </Avatar>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>Subscription Report</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.85 }}>
                                Every subscription charge in the month, new and renewal, from the billing cycles
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <IconButton aria-label="Previous month" onClick={() => setMonth(shiftMonth(month, -1))} sx={{ color: 'inherit' }}>
                            <ChevronLeftIcon />
                        </IconButton>
                        <TextField
                            type="month"
                            size="small"
                            value={month}
                            onChange={(e) => e.target.value && setMonth(e.target.value)}
                            inputProps={{ 'aria-label': 'Report month' }}
                            sx={{
                                bgcolor: 'rgba(255,255,255,.95)',
                                borderRadius: 1,
                                '& input': { py: 0.9 },
                            }}
                        />
                        <IconButton aria-label="Next month" onClick={() => setMonth(shiftMonth(month, 1))} sx={{ color: 'inherit' }}>
                            <ChevronRightIcon />
                        </IconButton>
                        <Button
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                            variant="contained"
                            disabled={loading}
                            onClick={loadReport}
                            sx={{ bgcolor: 'rgba(255,255,255,.2)', '&:hover': { bgcolor: 'rgba(255,255,255,.3)' } }}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {report && (
                <>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 6, md: 2.4 }}>
                            <StatCard icon={ReceiptIcon} label="Charges" value={report.charges} color="#1976d2"
                                caption={monthLabel(month)} />
                        </Grid>
                        <Grid size={{ xs: 6, md: 2.4 }}>
                            <StatCard icon={PersonAddIcon} label="New" value={report.newCharges} color="#26a69a"
                                caption="First charge of a subscription" />
                        </Grid>
                        <Grid size={{ xs: 6, md: 2.4 }}>
                            <StatCard icon={ReplayIcon} label="Renewals" value={report.renewalCharges} color="#8e44ad"
                                caption="Later periods" />
                        </Grid>
                        <Grid size={{ xs: 6, md: 2.4 }}>
                            <StatCard icon={MonetizationIcon} label="Gross charged" value={formatCurrency(report.grossCharged)} color="#ef6c00"
                                caption={`Square fees ${formatCurrency(report.squareFees)}`} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 2.4 }}>
                            <StatCard icon={WalletIcon} label="Net" value={formatCurrency(report.netCharged)} color="#2e7d32"
                                caption={report.chargesWithoutFee > 0 ? `${report.chargesWithoutFee} without a fee yet` : 'After Square fees'} />
                        </Grid>
                    </Grid>

                    {report.chargesWithoutAmount > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {report.chargesWithoutAmount} charge{report.chargesWithoutAmount === 1 ? ' has' : 's have'} no
                            recorded amount and {report.chargesWithoutAmount === 1 ? 'is' : 'are'} left out of the gross total.
                            Check them in Square.
                        </Alert>
                    )}
                    {report.chargesWithoutFee > 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {report.chargesWithoutFee} charge{report.chargesWithoutFee === 1 ? ' has' : 's have'} no Square fee
                            recorded yet, so {report.chargesWithoutFee === 1 ? 'it is' : 'they are'} left out of the fee and net totals.
                            New charges get theirs from Square's payment webhook within minutes; older ones need the fee backfill.
                        </Alert>
                    )}
                    {report.booksWithoutOrder > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {report.booksWithoutOrder} charge{report.booksWithoutOrder === 1 ? '' : 's'} included books
                            but {report.booksWithoutOrder === 1 ? 'has' : 'have'} no book order. Search for "NO BOOK ORDER" in the export.
                        </Alert>
                    )}

                    {report.byPlan?.length > 0 && (
                        <Paper elevation={0} sx={{ mb: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: 'action.hover' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Charges</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Gross charged</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Square fees</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {report.byPlan.map((p) => (
                                        <TableRow key={p.planId ?? p.planName}>
                                            <TableCell>{p.planName || `Plan ${p.planId}`}</TableCell>
                                            <TableCell align="right">{p.charges}</TableCell>
                                            <TableCell align="right">{formatCurrency(p.grossCharged)}</TableCell>
                                            <TableCell align="right">{formatCurrency(p.squareFees)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    )}

                    <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} spacing={1.5} sx={{ p: 2 }}>
                            <TextField
                                size="small"
                                placeholder="IBO, name, payment ID, book order"
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                                sx={{ minWidth: 300 }}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                            />
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={kind}
                                onChange={(_, v) => { if (v) { setKind(v); setPage(0); } }}
                            >
                                <ToggleButton value="ALL">All</ToggleButton>
                                <ToggleButton value="NEW">New</ToggleButton>
                                <ToggleButton value="RENEWAL">Renewal</ToggleButton>
                            </ToggleButtonGroup>
                            <Box sx={{ flex: 1 }} />
                            <Typography variant="caption" color="text.secondary">
                                {filtered
                                    ? `${rows.length} of ${report.rows.length} charges · ${formatCurrency(visibleTotal)}`
                                    : `${rows.length} charges`}
                            </Typography>
                            <Button variant="outlined" startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                                onClick={handleExport} disabled={exporting || report.charges === 0}>
                                Export CSV
                            </Button>
                        </Stack>
                        <TableContainer>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: 'action.hover' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Charged at</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>IBO</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Kind</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Charged</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Square fee</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>Net</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Cycle</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Book order</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Receipt</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {pageRows.map((r) => (
                                        <TableRow
                                            key={r.cycleId}
                                            hover
                                            tabIndex={0}
                                            onClick={() => setSelected(r)}
                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected(r); } }}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(r.chargedAt)}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.iboName || '—'}</Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                                    {r.iboNumber} · sub {r.subscriptionId}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{r.teamName || '—'}</TableCell>
                                            <TableCell>
                                                {r.planName || '—'}
                                                <Typography variant="caption" color="text.secondary" display="block">{r.periodKey}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" variant="outlined" label={r.chargeKind}
                                                    color={r.chargeKind === 'NEW' ? 'success' : 'secondary'} />
                                            </TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                {r.chargedAmount != null ? (
                                                    <Tooltip title={r.amountSource === 'CYCLE' ? 'From the billing cycle (no audit row)' : 'From the payment audit'}>
                                                        <span>{formatCurrency(r.chargedAmount)}</span>
                                                    </Tooltip>
                                                ) : (
                                                    <Chip size="small" color="warning" label="No amount" />
                                                )}
                                            </TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                {r.squareFee != null ? formatCurrency(r.squareFee) : (
                                                    <Typography variant="caption" color="text.disabled">Pending</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                                                {r.netAmount != null ? formatCurrency(r.netAmount) : (
                                                    <Typography variant="caption" color="text.disabled">—</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={r.cycleStatus === 'SHIPPED' ? 'Book order raised (or none was due), not parcel status' : ''}>
                                                    <Chip size="small" label={r.cycleStatus} color={cycleStatusColor(r.cycleStatus)} variant="outlined" />
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                {r.bookOrderNumber ? (
                                                    <>
                                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.bookOrderNumber}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {r.bookOrderStatus}
                                                            {r.bookDeliveredAt ? ` · delivered ${formatDate(r.bookDeliveredAt)}`
                                                                : r.bookShippedAt ? ` · shipped ${formatDate(r.bookShippedAt)}` : ''}
                                                        </Typography>
                                                    </>
                                                ) : r.bookNote ? (
                                                    <Chip size="small" color="warning" label="No book order" />
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled">No books</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {r.receiptUrl ? (
                                                    <Link href={r.receiptUrl} target="_blank" rel="noopener noreferrer" sx={{ fontFamily: 'monospace', fontSize: 12 }}
                                                        onClick={(e) => e.stopPropagation()}>
                                                        {r.receiptNumber || 'Receipt'}
                                                    </Link>
                                                ) : '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {rows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                                                {report.charges === 0 ? `No subscription charges in ${monthLabel(month)}` : 'No charges match'}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {rows.length > rowsPerPage && (
                            <TablePagination
                                component="div"
                                count={rows.length}
                                page={page}
                                onPageChange={(_, p) => setPage(p)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={[25, 50, 100, 250]}
                            />
                        )}
                    </Paper>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                        Click a charge to see everything recorded for it. Charged is gross; net is gross less
                        Square's fee, counted only where the fee is known. Month boundaries are server time.
                    </Typography>
                </>
            )}
            <SubscriptionChargeDetailsDialog charge={selected} onClose={() => setSelected(null)} />
        </Container>
    );
};

export default SubscriptionReportView;
